import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  BackLink,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Modal,
  Notice,
  PageHead,
  Panel,
  Select,
  Spinner,
  Table,
  TableWrap,
  Textarea,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { CustomFields } from '../CustomFields';
import { api, type Asset, type Category, type Location, type Person } from '../api';
import { useIBoonAIContext } from '../iboonAi';

type History = {
  id: string;
  personName: string;
  personEmail: string;
  assignedAt: string;
  assignedBy: string;
  returnedAt: string | null;
  returnedBy: string | null;
  reason: string;
  notes: string;
};

const when = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * One thing, and everywhere it has been.
 *
 * The history is most of the page. "Who had this before it broke" is the
 * question that actually gets asked, and a register that stores only the
 * current holder cannot answer it -- which is the main thing this has over
 * the spreadsheet it replaces.
 */
export function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [history, setHistory] = useState<History[]>([]);
  const [attached, setAttached] = useState<Array<{ id: string; name: string; state: string }>>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [editing, setEditing] = useState(false);
  const [attaching, setAttaching] = useState(false);
  useIBoonAIContext(
    'asset',
    asset && {
      id: asset.id,
      name: asset.name,
      assetTag: asset.assetTag,
      serialNumber: asset.serialNumber,
      state: asset.state,
      condition: asset.condition,
      categoryName: asset.categoryName,
      locationName: asset.locationName,
      heldById: asset.heldById,
      heldByName: asset.heldByName,
    },
  );
  const toast = useToast();

  const load = useCallback(async () => {
    const result = await api.get<{ asset: Asset; history: History[]; attached: typeof attached }>(
      `/api/assets/${id}`,
    );
    setAsset(result.asset);
    setHistory(result.history);
    setAttached(result.attached);
  }, [id]);

  useEffect(() => {
    load().catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load it.'),
    );
    api.get<{ people: Person[] }>('/api/people').then((r) => setPeople(r.people.filter((p) => p.active)));
  }, [load]);

  if (error) {
    return (
      <Notice tone="danger" title="Could not load that asset">
        {error}
      </Notice>
    );
  }
  if (!asset) return <Spinner />;

  return (
    <>
      <BackLink onClick={() => navigate('/assets')}>Back to the register</BackLink>

      <PageHead
        title={asset.name}
        subtitle={[asset.make, asset.model].filter(Boolean).join(' ') || asset.categoryName}
        actions={
          <>
            {asset.state === 'assigned' ? (
              <>
                <Button variant="primary" onClick={() => setAction('return')}>
                  Take it back
                </Button>
                {/* Straight to the next person. Returning first would mark it
                    available for a moment and record a trip to the store that
                    never happened. */}
                <Button variant="ghost" onClick={() => setAction('transfer')}>
                  Hand it on
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                disabled={asset.state === 'retired'}
                onClick={() => setAction('assign')}
              >
                Give it to somebody
              </Button>
            )}
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button variant="ghost" onClick={() => setAction('state')}>
              Mark as…
            </Button>
          </>
        }
      />

      <Panel title="What it is">
        <Row label="State" value={<Badge tone={asset.state === 'available' ? 'brand' : 'accent'}>{asset.state}</Badge>} />
        <Row label="Held by" value={asset.heldByName ?? <span className="muted">nobody</span>} />
        <Row label="Category" value={asset.categoryName} />
        <Row label="Asset tag" value={asset.assetTag ?? <span className="muted">none</span>} />
        <Row label="Serial number" value={asset.serialNumber ?? <span className="muted">none</span>} />
        <Row label="Condition" value={asset.condition} />
        <Row label="Where" value={asset.locationName ?? <span className="muted">unrecorded</span>} />
        {asset.warrantyUntil && <Row label="Warranty until" value={when(asset.warrantyUntil)} />}
        {asset.purchaseCost !== null && (
          <Row label="Cost" value={`₹${asset.purchaseCost.toLocaleString('en-IN')}`} />
        )}
        {/* The category's own questions, answered. Only the ones with an
            answer: a row reading "RAM: —" is noise on every other category. */}
        {Object.entries(asset.fields ?? {})
          .filter(([, value]) => value !== null && value !== '')
          .map(([key, value]) => (
            <Row key={key} label={key} value={String(value)} />
          ))}
      </Panel>

      {/*
        * What this is part of, and what is part of it.
        *
        * The tree is what makes "the hub in the laptop bag" answerable. A dock
        * with three things hanging off it is one object to carry and four to
        * account for, and a register that only knows the four cannot tell
        * anybody where they went.
        */}
      <Panel title="Plugged together">
        {asset.parentAssetId ? (
          <div className="line">
            <span className="line__label">Part of</span>
            <span>
              <Link to={`/assets/${asset.parentAssetId}`}>{asset.parentAssetName ?? 'something'}</Link>
            </span>
          </div>
        ) : (
          <div className="line">
            <span className="line__label">Part of</span>
            <span className="muted">nothing — it stands on its own</span>
          </div>
        )}

        {attached.length > 0 &&
          attached.map((child) => (
            <div key={child.id} className="line">
              <span className="line__label">Has</span>
              <span>
                <Link to={`/assets/${child.id}`}>{child.name}</Link>{' '}
                <span className="sub">{child.state}</span>
              </span>
            </div>
          ))}

        <div className="row-actions" style={{ marginTop: 10 }}>
          <Button variant="ghost" onClick={() => setAttaching(true)}>
            {asset.parentAssetId ? 'Plug it somewhere else' : 'Plug it into something'}
          </Button>
          {asset.parentAssetId && (
            <Button
              variant="ghost"
              onClick={() =>
                void api
                  .post(`/api/assets/${asset.id}/attach`, { parentAssetId: null })
                  .then(() => {
                    toast.show('Unplugged.');
                    void load();
                  })
                  .catch((caught: unknown) =>
                    toast.show(caught instanceof Error ? caught.message : 'That did not work.', {
                      tone: 'danger',
                    }),
                  )
              }
            >
              Unplug it
            </Button>
          )}
        </div>
      </Panel>

      <Panel title="Where it has been">
        {history.length === 0 ? (
          <p className="muted">Nobody has had it yet.</p>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Who</th>
                  <th>From</th>
                  <th>Until</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id}>
                    <td className="tcell-name">
                      {entry.personName}
                      <div className="sub">{entry.personEmail}</div>
                    </td>
                    <td>{when(entry.assignedAt)}</td>
                    <td>
                      {entry.returnedAt ? (
                        when(entry.returnedAt)
                      ) : (
                        <Badge tone="accent">still has it</Badge>
                      )}
                    </td>
                    <td className="muted">{entry.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Panel>

      {action && (
        <Decide
          asset={asset}
          people={people}
          action={action}
          onClose={() => setAction(null)}
          onDone={(message) => {
            toast.show(message);
            setAction(null);
            void load();
          }}
        />
      )}

      {attaching && (
        <Attach
          asset={asset}
          onClose={() => setAttaching(false)}
          onDone={() => {
            setAttaching(false);
            void load();
          }}
        />
      )}

      {editing && (
        <EditAsset
          asset={asset}
          onClose={() => setEditing(false)}
          onSaved={() => {
            toast.show('Saved.');
            setEditing(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="line">
      <span className="line__label">{label}</span>
      <span>{value}</span>
    </div>
  );
}

type Action = 'assign' | 'return' | 'transfer' | 'state';

const TITLE: Record<Action, string> = {
  assign: 'Give it to somebody',
  return: 'Take it back',
  transfer: 'Hand it on',
  state: 'Mark as…',
};

/**
 * The states that are not about a holder.
 *
 * Assigning and returning are absent on purpose: those move `state` and
 * `held_by` together, and offering "assigned" here would let the two disagree
 * -- an asset marked out with nobody holding it, which is the error this
 * register exists to stop. The server refuses it too.
 */
const STATES = [
  { id: 'available', label: 'Free — back on the shelf' },
  { id: 'repair', label: 'Away being fixed' },
  { id: 'lost', label: 'Lost — unaccounted for' },
  { id: 'retired', label: 'Retired — disposed of, kept for the record' },
] as const;

function Decide({
  asset,
  people,
  action,
  onClose,
  onDone,
}: {
  asset: Asset;
  people: Person[];
  action: Action;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  // Whoever has it is not a candidate to be handed it.
  const candidates = action === 'transfer' ? people.filter((p) => p.id !== asset.heldById) : people;

  const [personId, setPersonId] = useState(candidates[0]?.id ?? '');
  const [reason, setReason] = useState('');
  const [condition, setCondition] = useState(asset.condition);
  const [state, setState] = useState<string>(asset.state === 'available' ? 'repair' : 'available');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const named = () => candidates.find((p) => p.id === personId)?.name ?? 'them';

  async function go() {
    setBusy(true);
    try {
      if (action === 'assign') {
        await api.post(`/api/assets/${asset.id}/assign`, { personId, reason });
        onDone(`${asset.name} is with ${named()}.`);
      } else if (action === 'transfer') {
        await api.post(`/api/assets/${asset.id}/transfer`, { personId, reason });
        onDone(`${asset.name} is now with ${named()}.`);
      } else if (action === 'state') {
        await api.post(`/api/assets/${asset.id}/state`, { state });
        onDone(`${asset.name} is marked ${state}.`);
      } else {
        await api.post(`/api/assets/${asset.id}/return`, { condition, notes: reason });
        onDone(`${asset.name} is back.`);
      }
    } catch (caught) {
      // Shown as the server worded it. It refuses for real reasons -- somebody
      // already has this -- and rewording them hides which.
      toast.show(caught instanceof Error ? caught.message : 'That did not work.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  const needsPerson = action === 'assign' || action === 'transfer';

  return (
    <Modal open size="sm" title={TITLE[action]} onClose={onClose}>
      {needsPerson &&
        (candidates.length === 0 ? (
          <Notice tone="accent" title="Nobody to give it to">
            {people.length === 0
              ? 'The directory is empty. Refresh it from identity on the People page first.'
              : 'Everybody in the directory already has it, which cannot be true — refresh the directory.'}
          </Notice>
        ) : (
          <Field label="Who is taking it" required>
            <Select value={personId} onChange={(event) => setPersonId(event.target.value)}>
              {candidates.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name} — {person.email}
                </option>
              ))}
            </Select>
          </Field>
        ))}

      {action === 'return' && (
        <Field label="What state is it in" hint="Recorded against the asset, not the person.">
          <Select value={condition} onChange={(event) => setCondition(event.target.value)}>
            {['good', 'fair', 'damaged'].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {action === 'state' && (
        <>
          <Field label="Mark it as" required>
            <Select value={state} onChange={(event) => setState(event.target.value)}>
              {STATES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          {asset.heldByName && (
            <Notice tone="accent" title={`${asset.heldByName} has this`}>
              Marking it anything else takes it off them and closes their entry in the history.
            </Notice>
          )}
        </>
      )}

      {action !== 'state' && (
        <Field label={needsPerson ? 'Why (optional)' : 'Anything worth noting'}>
          <Textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      )}

      <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          disabled={busy || (needsPerson && !personId)}
          onClick={() => void go()}
        >
          {busy ? 'Saving…' : 'Do it'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Everything about an asset that is not about who has it.
 *
 * `state` and `heldBy` are absent, and the server will not accept them here
 * either -- they move together through assign, return and transfer so the two
 * can never disagree.
 */
function EditAsset({
  asset,
  onClose,
  onSaved,
}: {
  asset: Asset;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: asset.name,
    make: asset.make,
    model: asset.model,
    serialNumber: asset.serialNumber ?? '',
    assetTag: asset.assetTag ?? '',
    locationId: asset.locationId ?? '',
    notes: asset.notes ?? '',
    purchasedOn: asset.purchasedOn?.slice(0, 10) ?? '',
    purchaseCost: asset.purchaseCost === null ? '' : String(asset.purchaseCost),
    supplier: asset.supplier ?? '',
    invoiceRef: asset.invoiceRef ?? '',
    warrantyUntil: asset.warrantyUntil?.slice(0, 10) ?? '',
  });
  const [fields, setFields] = useState<Record<string, unknown>>(asset.fields ?? {});
  const [locations, setLocations] = useState<Location[]>([]);
  const [schema, setSchema] = useState<Category['fieldSchema']>([]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api.get<{ locations: Location[] }>('/api/locations').then((r) => setLocations(r.locations)).catch(() => {});
    api
      .get<{ categories: Category[] }>('/api/categories')
      .then((r) => setSchema(r.categories.find((c) => c.id === asset.categoryId)?.fieldSchema ?? []))
      .catch(() => {});
  }, [asset.categoryId]);

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((was) => ({ ...was, [key]: event.target.value }));

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/api/assets/${asset.id}`, {
        ...form,
        // A cleared cost is "unknown", not zero. Number('') is 0, which would
        // quietly record every blank as a free laptop.
        purchaseCost: form.purchaseCost === '' ? null : Number(form.purchaseCost),
        fields,
      });
      onSaved();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not save it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="md" title={`Edit ${asset.name}`} onClose={onClose}>
      <Field label="What is it" required>
        <Input value={form.name} onChange={set('name')} />
      </Field>
      <Field label="Make">
        <Input value={form.make} onChange={set('make')} />
      </Field>
      <Field label="Model">
        <Input value={form.model} onChange={set('model')} />
      </Field>
      <Field label="Serial number">
        <Input value={form.serialNumber} onChange={set('serialNumber')} />
      </Field>
      <Field label="Asset tag">
        <Input value={form.assetTag} onChange={set('assetTag')} />
      </Field>
      <Field label="Where it is">
        <Select value={form.locationId} onChange={set('locationId')}>
          <option value="">—</option>
          {locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Bought on">
        <Input type="date" value={form.purchasedOn} onChange={set('purchasedOn')} />
      </Field>
      <Field label="What it cost" hint="In rupees. Leave blank if nobody knows.">
        <Input type="number" value={form.purchaseCost} onChange={set('purchaseCost')} />
      </Field>
      <Field label="Bought from">
        <Input value={form.supplier} onChange={set('supplier')} />
      </Field>
      <Field label="Invoice">
        <Input value={form.invoiceRef} onChange={set('invoiceRef')} />
      </Field>
      <Field label="Warranty until" hint="Warnings appear a month before this date.">
        <Input type="date" value={form.warrantyUntil} onChange={set('warrantyUntil')} />
      </Field>

      <CustomFields schema={schema} values={fields} onChange={setFields} />

      <Field label="Notes">
        <Textarea rows={2} value={form.notes} onChange={set('notes')} />
      </Field>

      <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
        <Button variant="primary" disabled={busy || !form.name.trim()} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Plugging one thing into another.
 *
 * The list leaves out this asset and everything already inside it -- the
 * server refuses a loop anyway, and offering choices that will be refused is
 * a form that punishes somebody for reading it.
 */
function Attach({
  asset,
  onClose,
  onDone,
}: {
  asset: Asset;
  onClose: () => void;
  onDone: () => void;
}) {
  const [options, setOptions] = useState<Asset[]>([]);
  const [parentAssetId, setParentAssetId] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api
      .get<{ assets: Asset[] }>('/api/assets')
      .then((r) => {
        const usable = r.assets.filter(
          (candidate) => candidate.id !== asset.id && candidate.parentAssetId !== asset.id,
        );
        setOptions(usable);
        setParentAssetId(usable[0]?.id ?? '');
      })
      .catch(() => setOptions([]));
  }, [asset.id]);

  async function save() {
    setBusy(true);
    try {
      await api.post(`/api/assets/${asset.id}/attach`, { parentAssetId });
      toast.show('Plugged in.');
      onDone();
    } catch (caught) {
      // The refusal names the asset that closes the loop, which is the thing
      // somebody needs to know to unplug it.
      toast.show(caught instanceof Error ? caught.message : 'That did not work.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title={`What is ${asset.name} part of?`} onClose={onClose}>
      {options.length === 0 ? (
        <Notice tone="accent" title="Nothing to plug it into">
          There is nothing else in the register yet.
        </Notice>
      ) : (
        <>
          <Field label="Part of" required>
            <Select value={parentAssetId} onChange={(event) => setParentAssetId(event.target.value)}>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                  {option.assetTag ? ` — ${option.assetTag}` : ''}
                </option>
              ))}
            </Select>
          </Field>

          <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
            <Button variant="primary" disabled={busy || !parentAssetId} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Plug it in'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
